import React from 'react';
import PropTypes from 'prop-types';
import I18n from 'utils/I18n';
import { Button } from '@strava/ui/Button';
import { Switch } from '@strava/ui/Switch';
import createNetworkingClient from '../../utils/networking-client';
import styles from './CookieSettingsForm.scss';

const CookieSettingsForm = ({ allCookiesAccepted, isLoggedIn }) => {
  const [cookiePreferences, setCookiePreferences] = React.useState({
    performanceAndAnalytics: allCookiesAccepted,
    targetingAndMarketing: allCookiesAccepted
  });

  const handleCookieUpdate = (cookieType, value) => {
    setCookiePreferences({
      ...cookiePreferences,
      [cookieType]: value
    });
  };

  // TODO: This is _not_ the final implementation of the cookie preference
  // update logic. We'll need to update this method to use the new field names
  // once they're available. Currently, this just updates the existing preference
  // field in a way that won't break anything.
  const handleSave = async () => {
    const preference =
      cookiePreferences.performanceAndAnalytics &&
      cookiePreferences.targetingAndMarketing;

    if (!isLoggedIn) {
      const cookieExpiration = new Date();
      cookieExpiration.setFullYear(cookieExpiration.getFullYear() + 1);

      let domain;
      if (/localhost/.test(window.location.hostname)) {
        domain = 'localhost';
      } else if (/staging\./.test(window.location.hostname)) {
        domain = '.staging.strava.com';
      } else {
        domain = '.strava.com';
      }

      document.cookie =
        `_strava_cbv3=${preference};` +
        `expires=${cookieExpiration.toString()};` +
        `domain=${domain};` +
        `path=/;`;

      return;
    }

    await createNetworkingClient().put('/frontend/athlete/preferences', {
      allow_nonessential_cookies_v3: preference
    });
  };

  return (
    <div>
      <h1>{I18n.t('legal.cookie_settings.title')}</h1>
      <p>
        {I18n.t_html('legal.cookie_settings.description_html', {
          cookie_policy_url: 'https://www.strava.com/legal/cookie_policy'
        })}
      </p>

      <hr />

      <h4>{I18n.t('legal.cookie_settings.performance_and_analytics.title')}</h4>
      <div className={styles.wrapper}>
        <p>
          {I18n.t(
            'legal.cookie_settings.performance_and_analytics.description'
          )}
        </p>
        <Switch
          className={styles.switch}
          checked={cookiePreferences.performanceAndAnalytics}
          onClick={() => {
            handleCookieUpdate(
              'performanceAndAnalytics',
              !cookiePreferences.performanceAndAnalytics
            );
          }}
        />
      </div>

      <hr />

      <h4>{I18n.t('legal.cookie_settings.targeting_and_marketing.title')}</h4>
      <div className={styles.wrapper}>
        <p>
          {I18n.t('legal.cookie_settings.targeting_and_marketing.description')}
        </p>
        <Switch
          className={styles.switch}
          checked={cookiePreferences.targetingAndMarketing}
          onClick={() => {
            handleCookieUpdate(
              'targetingAndMarketing',
              !cookiePreferences.targetingAndMarketing
            );
          }}
        />
      </div>

      <Button variant="primary" onClick={handleSave} className={styles.button}>
        {I18n.t('legal.cookie_settings.save_preferences')}
      </Button>
    </div>
  );
};

CookieSettingsForm.propTypes = {
  allCookiesAccepted: PropTypes.bool,
  isLoggedIn: PropTypes.bool
};

const CookieSettingsFormWrapper = ({
  allowNonessentialCookies,
  isLoggedIn
}) => {
  let allCookiesAccepted = allowNonessentialCookies;

  if (allowNonessentialCookies === null) {
    // If the user is not logged in, check for the cookie value that indicates
    // if they have accepted non essential cookies.
    const cookie =
      document.cookie
        .split(';')
        .find((c) => c.trim().startsWith('_strava_cbv3='))
        ?.split('=')[1] ?? null;

    allCookiesAccepted = cookie && cookie === 'true';
  }

  return (
    <CookieSettingsForm
      allCookiesAccepted={allCookiesAccepted}
      isLoggedIn={isLoggedIn}
    />
  );
};

CookieSettingsFormWrapper.propTypes = {
  allowNonessentialCookies: PropTypes.bool,
  isLoggedIn: PropTypes.bool
};

export default CookieSettingsFormWrapper;
